import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Users, Plus, Trash2, Edit2, Eye, Ban, Check, X, BarChart3, Copy, RefreshCw, DollarSign, Coins, Send } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#1a1a1a] border border-white/10 rounded-lg px-3 py-2 text-xs">
      <p className="text-[#A1A1AA] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}</p>
      ))}
    </div>
  );
};

const AgencyManager = () => {
  const { token } = useAuth();
  const [subAccounts, setSubAccounts] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [subAnalytics, setSubAnalytics] = useState(null);
  const [editingSub, setEditingSub] = useState(null);
  const [copiedPwd, setCopiedPwd] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [showBilling, setShowBilling] = useState(false);
  const [addCreditsTarget, setAddCreditsTarget] = useState(null);
  const [addCreditsAmount, setAddCreditsAmount] = useState(500);

  const [form, setForm] = useState({
    email: '', company_name: '', contact_name: '', max_credits: 1000, notes: ''
  });
  const [editForm, setEditForm] = useState({
    company_name: '', contact_name: '', max_credits: 0, notes: ''
  });

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [subsRes, overviewRes] = await Promise.all([
        axios.get(`${API_URL}/api/agency/sub-accounts`, authHeaders),
        axios.get(`${API_URL}/api/agency/overview`, authHeaders)
      ]);
      setSubAccounts(subsRes.data);
      setOverview(overviewRes.data);
    } catch (err) {
      console.error('Agency fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createSubAccount = async () => {
    if (!form.email || !form.company_name) return;
    try {
      const res = await axios.post(`${API_URL}/api/agency/sub-accounts`, form, authHeaders);
      setSubAccounts(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({ email: '', company_name: '', contact_name: '', max_credits: 1000, notes: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella creazione');
    }
  };

  const deleteSubAccount = async (subId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo sub-account?')) return;
    try {
      await axios.delete(`${API_URL}/api/agency/sub-accounts/${subId}`, authHeaders);
      setSubAccounts(prev => prev.filter(s => s.id !== subId));
      if (selectedSub?.id === subId) setSelectedSub(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const toggleStatus = async (sub) => {
    const newStatus = sub.status === 'active' ? 'suspended' : 'active';
    try {
      await axios.put(`${API_URL}/api/agency/sub-accounts/${sub.id}`, { status: newStatus }, authHeaders);
      setSubAccounts(prev => prev.map(s => s.id === sub.id ? { ...s, status: newStatus } : s));
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const updateSubAccount = async () => {
    if (!editingSub) return;
    try {
      await axios.put(`${API_URL}/api/agency/sub-accounts/${editingSub.id}`, editForm, authHeaders);
      setEditingSub(null);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  const viewAnalytics = async (sub) => {
    setSelectedSub(sub);
    try {
      const res = await axios.get(`${API_URL}/api/agency/sub-accounts/${sub.id}/analytics?days=30`, authHeaders);
      setSubAnalytics(res.data);
    } catch (err) {
      console.error('Analytics error:', err);
    }
  };

  const startEdit = (sub) => {
    setEditingSub(sub);
    setEditForm({
      company_name: sub.company_name,
      contact_name: sub.contact_name || '',
      max_credits: sub.max_credits,
      notes: sub.notes || ''
    });
  };

  const copyPassword = async (pwd) => {
    try {
      await navigator.clipboard.writeText(pwd);
      setCopiedPwd(pwd);
      setTimeout(() => setCopiedPwd(null), 2000);
    } catch {
      window.prompt('Password temporanea:', pwd);
    }
  };

  const fetchBilling = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/agency/billing?days=30`, authHeaders);
      setBillingData(res.data);
      setShowBilling(true);
    } catch (err) {
      console.error('Billing error:', err);
    }
  };

  const addCreditsToSub = async (subId) => {
    if (!addCreditsAmount || addCreditsAmount <= 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/agency/sub-accounts/${subId}/add-credits`,
        { amount: addCreditsAmount }, authHeaders);
      setAddCreditsTarget(null);
      setAddCreditsAmount(500);
      fetchData();
      alert(`${res.data.credits_added} crediti aggiunti! Saldo agenzia: ${res.data.agency_remaining}`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore');
    }
  };

  if (loading) {
    return (
      <div className="py-12 text-center">
        <div className="w-8 h-8 border-2 border-[#0055FF] border-t-transparent rounded-full animate-spin mx-auto" />
        <p className="text-[#A1A1AA] mt-4 text-sm">Caricamento sub-account...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-manager">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-[#6366F1]" />
          <h2 className="font-['Outfit'] text-lg font-medium">Gestione Agenzia</h2>
        </div>
        <Button
          onClick={() => setShowCreate(!showCreate)}
          className="bg-[#0055FF] hover:bg-[#3377FF] text-white text-xs rounded-full"
          data-testid="create-sub-account-btn"
        >
          <Plus className="w-3 h-3 mr-1" /> Nuovo Sub-account
        </Button>
        <Button
          onClick={fetchBilling}
          variant="outline"
          className="border-white/10 text-xs"
          data-testid="agency-billing-btn"
        >
          <DollarSign className="w-3 h-3 mr-1" /> Billing
        </Button>
      </div>

      {/* Overview Stats */}
      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Sub-account', value: overview.total_sub_accounts, color: '#0055FF' },
            { label: 'Attivi', value: overview.active, color: '#10B981' },
            { label: 'Sospesi', value: overview.suspended, color: '#EF4444' },
            { label: 'Crediti Allocati', value: overview.total_credits_allocated, color: '#F59E0B' }
          ].map((s, i) => (
            <div key={i} className="bg-[#0a0a0a] rounded-xl p-4 border border-white/5">
              <span className="text-[10px] text-[#A1A1AA] uppercase tracking-wider block mb-1">{s.label}</span>
              <span className="text-2xl font-['Outfit'] font-semibold" style={{ color: s.color }}>{s.value.toLocaleString()}</span>
            </div>
          ))}
        </div>
      )}

      {/* Create Form */}
      {showCreate && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-[#0055FF]/30 space-y-4" data-testid="create-sub-form">
          <h4 className="text-sm font-medium">Nuovo Sub-account</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Email *</label>
              <Input
                value={form.email}
                onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                placeholder="cliente@esempio.com"
                className="bg-white/5 border-white/10 text-sm"
                data-testid="sub-email-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Azienda *</label>
              <Input
                value={form.company_name}
                onChange={e => setForm(p => ({ ...p, company_name: e.target.value }))}
                placeholder="Nome Azienda"
                className="bg-white/5 border-white/10 text-sm"
                data-testid="sub-company-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Contatto</label>
              <Input
                value={form.contact_name}
                onChange={e => setForm(p => ({ ...p, contact_name: e.target.value }))}
                placeholder="Nome contatto"
                className="bg-white/5 border-white/10 text-sm"
                data-testid="sub-contact-input"
              />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Crediti Max</label>
              <Input
                type="number"
                value={form.max_credits}
                onChange={e => setForm(p => ({ ...p, max_credits: parseInt(e.target.value) || 0 }))}
                className="bg-white/5 border-white/10 text-sm"
                data-testid="sub-credits-input"
              />
            </div>
          </div>
          <div>
            <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Note</label>
            <Input
              value={form.notes}
              onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
              placeholder="Note opzionali..."
              className="bg-white/5 border-white/10 text-sm"
              data-testid="sub-notes-input"
            />
          </div>
          <div className="flex gap-2">
            <Button onClick={createSubAccount} className="bg-[#10B981] hover:bg-[#059669] text-white text-xs" data-testid="confirm-create-sub-btn">
              <Check className="w-3 h-3 mr-1" /> Crea
            </Button>
            <Button onClick={() => setShowCreate(false)} variant="outline" className="border-white/10 text-xs" data-testid="cancel-create-sub-btn">
              <X className="w-3 h-3 mr-1" /> Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingSub && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-[#F59E0B]/30 space-y-4" data-testid="edit-sub-form">
          <h4 className="text-sm font-medium">Modifica: {editingSub.company_name}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Azienda</label>
              <Input value={editForm.company_name} onChange={e => setEditForm(p => ({ ...p, company_name: e.target.value }))} className="bg-white/5 border-white/10 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Contatto</label>
              <Input value={editForm.contact_name} onChange={e => setEditForm(p => ({ ...p, contact_name: e.target.value }))} className="bg-white/5 border-white/10 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Crediti Max</label>
              <Input type="number" value={editForm.max_credits} onChange={e => setEditForm(p => ({ ...p, max_credits: parseInt(e.target.value) || 0 }))} className="bg-white/5 border-white/10 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-[#A1A1AA] uppercase block mb-1">Note</label>
              <Input value={editForm.notes} onChange={e => setEditForm(p => ({ ...p, notes: e.target.value }))} className="bg-white/5 border-white/10 text-sm" />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={updateSubAccount} className="bg-[#F59E0B] hover:bg-[#D97706] text-black text-xs">
              <Check className="w-3 h-3 mr-1" /> Salva
            </Button>
            <Button onClick={() => setEditingSub(null)} variant="outline" className="border-white/10 text-xs">
              <X className="w-3 h-3 mr-1" /> Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Billing Panel */}
      {showBilling && billingData && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-[#F59E0B]/30 space-y-4" data-testid="billing-panel">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[#F59E0B]" /> Billing (ultimi {billingData.period_days}gg)
            </h4>
            <button onClick={() => setShowBilling(false)} className="text-[#A1A1AA] hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Crediti Usati', value: billingData.total_credits_used, color: '#0055FF' },
              { label: 'Costo AI (USD)', value: `$${billingData.total_ai_cost_usd}`, color: '#EF4444' },
              { label: 'Costo AI (EUR)', value: `€${billingData.total_ai_cost_eur}`, color: '#F59E0B' },
              { label: 'Media/Sub', value: billingData.avg_credits_per_sub, color: '#6366F1' }
            ].map((s, i) => (
              <div key={i} className="bg-black/30 rounded-lg p-3 text-center">
                <span className="text-[9px] text-[#A1A1AA] uppercase block mb-1">{s.label}</span>
                <span className="text-lg font-['Outfit'] font-semibold" style={{ color: s.color }}>{typeof s.value === 'number' ? s.value.toLocaleString() : s.value}</span>
              </div>
            ))}
          </div>

          {billingData.billing?.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[#A1A1AA] text-left border-b border-white/10">
                    <th className="py-2 px-2">Azienda</th>
                    <th className="py-2 px-2">Stato</th>
                    <th className="py-2 px-2">Max Cr.</th>
                    <th className="py-2 px-2">Usati (periodo)</th>
                    <th className="py-2 px-2">Costo AI</th>
                    <th className="py-2 px-2">Utilizzo %</th>
                  </tr>
                </thead>
                <tbody>
                  {billingData.billing.map((row, i) => (
                    <tr key={i} className="border-b border-white/5">
                      <td className="py-2 px-2 font-medium">{row.company_name}</td>
                      <td className="py-2 px-2"><span className={`text-[9px] px-1.5 py-0.5 rounded-full ${row.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>{row.status}</span></td>
                      <td className="py-2 px-2">{row.max_credits?.toLocaleString()}</td>
                      <td className="py-2 px-2 font-mono">{row.credits_used_period}</td>
                      <td className="py-2 px-2 text-[#EF4444]">${row.ai_cost_usd}</td>
                      <td className="py-2 px-2">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-white/5 rounded-full overflow-hidden">
                            <div className="h-full rounded-full" style={{
                              width: `${Math.min(100, row.utilization_pct)}%`,
                              backgroundColor: row.utilization_pct > 80 ? '#EF4444' : row.utilization_pct > 50 ? '#F59E0B' : '#10B981'
                            }} />
                          </div>
                          <span className="text-[9px]">{row.utilization_pct}%</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Add Credits Modal */}
      {addCreditsTarget && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-[#10B981]/30 space-y-3" data-testid="add-credits-modal">
          <h4 className="text-sm font-medium flex items-center gap-2">
            <Coins className="w-4 h-4 text-[#10B981]" /> Aggiungi crediti: {addCreditsTarget.company_name}
          </h4>
          <p className="text-xs text-[#A1A1AA]">Trasferisci crediti dal tuo wallet al sub-account.</p>
          <div className="flex items-center gap-3">
            <Input
              type="number"
              value={addCreditsAmount}
              onChange={e => setAddCreditsAmount(parseInt(e.target.value) || 0)}
              placeholder="Quantita'"
              className="bg-white/5 border-white/10 text-sm w-32"
              data-testid="add-credits-amount"
            />
            <Button onClick={() => addCreditsToSub(addCreditsTarget.id)} className="bg-[#10B981] hover:bg-[#059669] text-white text-xs" data-testid="confirm-add-credits-btn">
              <Send className="w-3 h-3 mr-1" /> Trasferisci
            </Button>
            <Button onClick={() => setAddCreditsTarget(null)} variant="outline" className="border-white/10 text-xs">
              <X className="w-3 h-3 mr-1" /> Annulla
            </Button>
          </div>
        </div>
      )}

      {/* Sub-accounts List */}
      {subAccounts.length === 0 ? (
        <div className="bg-[#0a0a0a] rounded-xl p-12 border border-white/5 text-center">
          <Users className="w-16 h-16 text-[#A1A1AA] mx-auto mb-4 opacity-40" />
          <h3 className="font-['Outfit'] text-lg font-medium mb-2">Nessun sub-account</h3>
          <p className="text-sm text-[#A1A1AA] mb-4">Crea il primo sub-account per i tuoi clienti.</p>
          <Button onClick={() => setShowCreate(true)} className="bg-[#0055FF] hover:bg-[#3377FF] text-white text-xs rounded-full" data-testid="empty-create-sub-btn">
            <Plus className="w-3 h-3 mr-1" /> Crea Sub-account
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {subAccounts.map(sub => (
            <div
              key={sub.id}
              className={`bg-[#0a0a0a] rounded-xl p-4 border transition-colors ${
                selectedSub?.id === sub.id ? 'border-[#0055FF]/50' : 'border-white/5 hover:border-white/10'
              }`}
              data-testid={`sub-account-${sub.id}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold ${
                    sub.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'
                  }`}>
                    {sub.company_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium text-sm truncate">{sub.company_name}</h4>
                      <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${
                        sub.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'
                      }`}>
                        {sub.status === 'active' ? 'Attivo' : 'Sospeso'}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#A1A1AA] truncate">{sub.email}</p>
                  </div>
                </div>

                <div className="flex items-center gap-4 flex-shrink-0">
                  <div className="text-right hidden sm:block">
                    <span className="text-xs font-medium block">{sub.credits_used || 0} / {sub.max_credits}</span>
                    <span className="text-[9px] text-[#A1A1AA]">crediti usati</span>
                  </div>

                  {sub.temp_password && (
                    <button
                      onClick={() => copyPassword(sub.temp_password)}
                      className="text-[#F59E0B] hover:text-[#D97706] p-1"
                      title="Copia password temporanea"
                      data-testid={`copy-pwd-${sub.id}`}
                    >
                      {copiedPwd === sub.temp_password ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}

                  <button onClick={() => viewAnalytics(sub)} className="text-[#0055FF] hover:text-[#3377FF] p-1" title="Analytics" data-testid={`view-analytics-${sub.id}`}>
                    <BarChart3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAddCreditsTarget(sub)} className="text-[#10B981] hover:text-[#059669] p-1" title="Aggiungi crediti" data-testid={`add-credits-${sub.id}`}>
                    <Coins className="w-4 h-4" />
                  </button>
                  <button onClick={() => startEdit(sub)} className="text-[#A1A1AA] hover:text-white p-1" title="Modifica" data-testid={`edit-sub-${sub.id}`}>
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button onClick={() => toggleStatus(sub)} className={`p-1 ${sub.status === 'active' ? 'text-[#EF4444] hover:text-[#DC2626]' : 'text-[#10B981] hover:text-[#059669]'}`} title={sub.status === 'active' ? 'Sospendi' : 'Riattiva'} data-testid={`toggle-status-${sub.id}`}>
                    {sub.status === 'active' ? <Ban className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
                  </button>
                  <button onClick={() => deleteSubAccount(sub.id)} className="text-[#EF4444] hover:text-[#DC2626] p-1" title="Elimina" data-testid={`delete-sub-${sub.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {sub.contact_name && (
                <div className="mt-2 text-[10px] text-[#A1A1AA]">Contatto: {sub.contact_name}</div>
              )}
              {sub.notes && (
                <div className="mt-1 text-[10px] text-[#A1A1AA] italic">"{sub.notes}"</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Selected Sub-account Analytics */}
      {selectedSub && subAnalytics && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-[#0055FF]/30 space-y-4" data-testid="sub-analytics-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-[#0055FF]" />
              <h4 className="text-sm font-medium">Analytics: {subAnalytics.sub_account?.company_name}</h4>
            </div>
            <button onClick={() => { setSelectedSub(null); setSubAnalytics(null); }} className="text-[#A1A1AA] hover:text-white">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div className="text-center">
              <span className="text-xl font-['Outfit'] font-bold text-[#0055FF]">{subAnalytics.total_calls}</span>
              <p className="text-[9px] text-[#A1A1AA]">Chiamate Totali</p>
            </div>
            <div className="text-center">
              <span className="text-xl font-['Outfit'] font-bold text-[#10B981]">{subAnalytics.credits_used}</span>
              <p className="text-[9px] text-[#A1A1AA]">Crediti Usati</p>
            </div>
            <div className="text-center">
              <span className="text-xl font-['Outfit'] font-bold text-[#F59E0B]">{subAnalytics.credits_limit}</span>
              <p className="text-[9px] text-[#A1A1AA]">Crediti Limite</p>
            </div>
          </div>

          {subAnalytics.daily_calls?.length > 0 && (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={subAnalytics.daily_calls.map(d => ({ ...d, date: d.date.substring(5) }))}>
                <XAxis dataKey="date" tick={{ fontSize: 9, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 10, fill: '#A1A1AA' }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="calls" name="Chiamate" fill="#0055FF" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}

          {subAnalytics.calls_by_type?.length > 0 && (
            <div className="space-y-1">
              <span className="text-[10px] text-[#A1A1AA] uppercase">Per Tipo</span>
              {subAnalytics.calls_by_type.map((t, i) => (
                <div key={i} className="flex items-center justify-between text-xs py-1 border-b border-white/5 last:border-0">
                  <span className="text-[#A1A1AA]">{t.type}</span>
                  <span className="font-medium">{t.count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AgencyManager;
